import moment from 'moment-timezone';
import { asTimeZone, fromUtcToStartDateMoment } from '../utils/dateFormatter';
import { create, updateField, updateFields, update, remove, getById, getAll, getBySingleQuery, getByIdQuery, getByDoubleQuery, getByQuery } from '../utils/dbUtils';
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNullOrUndefined, isNumberGreaterThanOrEqualToZero, isNumberNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import Customer from './Customer';
import StatusHistory from './StatusHistory';
import PrescriptionGroup from './PrescriptionGroup';
import PrescriptionAddOnDetails from './PrescriptionAddOnDetails';
import { collection, doc, documentId, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { toE164PhoneNumber, toPhoneNumberLocal } from '../utils/stringFormatter';
import { toLower } from 'lodash';

class Prescription {
    static collectionName = 'prescriptions';

    eRXId = null; // electronic prescription id such as surescripts id
    refId = null; // prescription id in foreign EHR
    userRefId = null; // user reference id in foreign EHR

    userId = null; // userId of the customer
    customer = new Customer(null).toDto(); // customer details snapshot

    practiceId = null; // prescribing practice
    pharmacyId = null; // fulfilling pharmacy for the prescription

    providerId = null; // userId of the provider who prescribed the medication
    providerFirstName = null;
    providerLastName = null;
    providerNPI = null;

    fillAt = null; // earliest date at which the prescription can be submitted/sent over to the pharmacy to be filled
    fillId = null; // fill request id which will bundle multiple prescriptions together for the same pharmacy order/shipment

    medicationId = null;
    name = null; // medication/product name
    quantity = 1;
    instructions = null;
    strength = null;
    form = null; // form of the medication (e.g., tablet, liquid)
    route = null; // route of administration (e.g., oral, intravenous)
    count = 0;
    countUnits = null;
    daysSupply = 0; // days of medication this fill covers; 0 means unset
    refills = 0;
    medRefId = null;
    productId = null;
    variantId = null;
    addOns = [];

    pdfRef = null; // storage path for the written prescription pdf
    providerSignatureRef = null; // storage path for the prescriber's signature image

    status = "PENDING"; // enum PrescriptionStatuses REQUESTED, WRITTEN, SUBMITTED, FILLED, SHIPPED, DELIVERED, VOIDED
    statusHistory = []; // list of StatusHistory, historical changes to status

    fileRefId = null; // file/photo of the prescription

    requestedAt = null; // date the patient requested the medication
    writtenAt = null; // date the prescription was written (and therefore approved by prescriber)
    submittedAt = null; // date prescription is submitted to the pharmacy to be filled
    filledAt = null; // date that prescription is filled by pharmacy (but not shipped to customer yet)
    shippedAt = null; // date that pharmacy shipped the prescription
    deliveredAt = null; // date that patient received their prescription
    completedAt = null; // when prescription is delivered and paid for
    voidedAt = null; // voided because couldnt be fulfilled, delivered, was lost and couldn't be completed, or any other reason
    expirationDate = null; // expiration by which the prescription must be filled

    isApproved = false;
    isShipped = false;
    isDelivered = false;
    isPaid = false;
    isCompleted = false;
    isVoided = false;
    isControlled = false; // indicates if the prescription is for a controlled substance

    carrier = null; // shipping carrier name
    trackingNumber = null; // shipment tracking number
    tracking = null;

    prescriptionGroupId = null; // parent prescription group that this prescription belongs to
    fillNumber = 0; // 1-based fill number within the prescription group (1 = first fill, 2+ = refill)

    orderId = null;
    subscriptionId = null;
    programId = null;
    encounterId = null;

    testMode = false;

    constructor(prescription) {
        if (isObjectNotEmpty(prescription)) {
            if (isStringNotEmpty(prescription.eRXId)) { this.eRXId = prescription.eRXId; }
            if (isStringNotEmpty(prescription.refId)) { this.refId = prescription.refId; }
            if (isStringNotEmpty(prescription.userRefId)) { this.userRefId = prescription.userRefId; }

            if (isStringNotEmpty(prescription.userId)) { this.userId = prescription.userId; }
            if (isObjectNotEmpty(prescription.customer)) { this.customer = new Customer(prescription.customer).toDto(); }

            if (isStringNotEmpty(prescription.practiceId)) { this.practiceId = prescription.practiceId; }
            if (isStringNotEmpty(prescription.pharmacyId)) { this.pharmacyId = prescription.pharmacyId; }

            if (isStringNotEmpty(prescription.providerId)) { this.providerId = prescription.providerId; }
            if (isStringNotEmpty(prescription.providerFirstName)) { this.providerFirstName = prescription.providerFirstName; }
            if (isStringNotEmpty(prescription.providerLastName)) { this.providerLastName = prescription.providerLastName; }
            if (isStringNotEmpty(prescription.providerNPI)) { this.providerNPI = prescription.providerNPI; }

            if (isNotNullOrUndefined(prescription.fillAt)) { this.fillAt = prescription.fillAt; }
            if (isStringNotEmpty(prescription.fillId)) { this.fillId = prescription.fillId; }

            if (isStringNotEmpty(prescription.medicationId)) { this.medicationId = prescription.medicationId; }
            if (isStringNotEmpty(prescription.name)) { this.name = prescription.name; }
            if (isNumberGreaterThanOrEqualToZero(prescription.quantity)) { this.quantity = prescription.quantity; }
            if (isStringNotEmpty(prescription.instructions)) { this.instructions = prescription.instructions; }
            if (isStringNotEmpty(prescription.strength)) { this.strength = prescription.strength; }
            if (isStringNotEmpty(prescription.form)) { this.form = prescription.form; }
            if (isStringNotEmpty(prescription.route)) { this.route = prescription.route; }
            if (isNumberGreaterThanOrEqualToZero(prescription.count)) { this.count = prescription.count; }
            if (isStringNotEmpty(prescription.countUnits)) { this.countUnits = prescription.countUnits; }
            if (isNumberGreaterThanOrEqualToZero(prescription.daysSupply)) { this.daysSupply = prescription.daysSupply; }
            if (isNumberGreaterThanOrEqualToZero(prescription.refills)) { this.refills = prescription.refills; }
            if (isStringNotEmpty(prescription.medRefId)) { this.medRefId = prescription.medRefId; }
            if (isStringNotEmpty(prescription.productId)) { this.productId = prescription.productId; }
            if (isStringNotEmpty(prescription.variantId)) { this.variantId = prescription.variantId; }
            if (isListNotEmpty(prescription.addOns)) { this.addOns = prescription.addOns.map((addOn) => new PrescriptionAddOnDetails(addOn).toDto()); }
            if (isStringNotEmpty(prescription.pdfRef)) { this.pdfRef = prescription.pdfRef; }
            if (isStringNotEmpty(prescription.providerSignatureRef)) { this.providerSignatureRef = prescription.providerSignatureRef; }

            if (isStringNotEmpty(prescription.status)) { this.status = prescription.status; }
            if (isListNotEmpty(prescription.statusHistory)) { this.statusHistory = prescription.statusHistory; }
            else if (isStringNotEmpty(this.status)) { this.statusHistory = StatusHistory.addNewStatus(this.status); }

            if (isStringNotEmpty(prescription.fileRefId)) { this.fileRefId = prescription.fileRefId; }

            if (isNotNullOrUndefined(prescription.requestedAt)) { this.requestedAt = prescription.requestedAt; }
            if (isNotNullOrUndefined(prescription.writtenAt)) { this.writtenAt = prescription.writtenAt; }
            if (isNotNullOrUndefined(prescription.submittedAt)) { this.submittedAt = prescription.submittedAt; }
            if (isNotNullOrUndefined(prescription.filledAt)) { this.filledAt = prescription.filledAt; }
            if (isNotNullOrUndefined(prescription.shippedAt)) { this.shippedAt = prescription.shippedAt; }
            if (isNotNullOrUndefined(prescription.deliveredAt)) { this.deliveredAt = prescription.deliveredAt; }
            if (isNotNullOrUndefined(prescription.completedAt)) { this.completedAt = prescription.completedAt; }
            if (isNotNullOrUndefined(prescription.voidedAt)) { this.voidedAt = prescription.voidedAt; }
            if (isNotNullOrUndefined(prescription.expirationDate)) { this.expirationDate = prescription.expirationDate; }

            if (isBooleanTrue(prescription.isApproved)) { this.isApproved = true; }
            if (isBooleanTrue(prescription.isShipped)) { this.isShipped = true; }
            if (isBooleanTrue(prescription.isDelivered)) { this.isDelivered = true; }
            if (isBooleanTrue(prescription.isPaid)) { this.isPaid = true; }
            if (isBooleanTrue(prescription.isCompleted)) { this.isCompleted = true; }
            if (isBooleanTrue(prescription.isVoided)) { this.isVoided = true; }
            if (isBooleanTrue(prescription.isControlled)) { this.isControlled = true; }

            if (isStringNotEmpty(prescription.carrier)) { this.carrier = prescription.carrier; }
            if (isStringNotEmpty(prescription.trackingNumber)) { this.trackingNumber = prescription.trackingNumber; }
            if (isObjectNotEmpty(prescription.tracking)) { this.tracking = prescription.tracking; }

            if (isStringNotEmpty(prescription.prescriptionGroupId)) { this.prescriptionGroupId = prescription.prescriptionGroupId; }
            if (isNumberGreaterThanOrEqualToZero(prescription.fillNumber)) { this.fillNumber = prescription.fillNumber; }

            if (isStringNotEmpty(prescription.orderId)) { this.orderId = prescription.orderId; }
            if (isStringNotEmpty(prescription.subscriptionId)) { this.subscriptionId = prescription.subscriptionId; }
            if (isStringNotEmpty(prescription.programId)) { this.programId = prescription.programId; }
            if (isStringNotEmpty(prescription.encounterId)) { this.encounterId = prescription.encounterId; }

            if (isBooleanTrue(prescription.testMode)) { this.testMode = true; }
        }
    }

    toDto = () => {
        return {
            eRXId: this.eRXId,
            refId: this.refId,
            userRefId: this.userRefId,

            userId: this.userId,
            customer: this.customer,

            practiceId: this.practiceId,
            pharmacyId: this.pharmacyId,

            providerId: this.providerId,
            providerFirstName: this.providerFirstName,
            providerLastName: this.providerLastName,
            providerNPI: this.providerNPI,

            fillAt: this.fillAt,
            fillId: this.fillId,

            medicationId: this.medicationId,
            name: this.name,
            quantity: this.quantity,
            instructions: this.instructions,
            strength: this.strength,
            form: this.form,
            route: this.route,
            count: this.count,
            countUnits: this.countUnits,
            daysSupply: this.daysSupply,
            refills: this.refills,
            medRefId: this.medRefId,
            productId: this.productId,
            variantId: this.variantId,
            addOns: this.addOns,
            pdfRef: this.pdfRef,
            providerSignatureRef: this.providerSignatureRef,

            status: this.status,
            statusHistory: this.statusHistory,

            fileRefId: this.fileRefId,

            requestedAt: this.requestedAt,
            writtenAt: this.writtenAt,
            submittedAt: this.submittedAt,
            filledAt: this.filledAt,
            shippedAt: this.shippedAt,
            deliveredAt: this.deliveredAt,
            completedAt: this.completedAt,
            voidedAt: this.voidedAt,
            expirationDate: this.expirationDate,

            isApproved: this.isApproved,
            isShipped: this.isShipped,
            isDelivered: this.isDelivered,
            isPaid: this.isPaid,
            isCompleted: this.isCompleted,
            isVoided: this.isVoided,
            isControlled: this.isControlled,

            carrier: this.carrier,
            trackingNumber: this.trackingNumber,
            tracking: this.tracking,

            prescriptionGroupId: this.prescriptionGroupId,
            fillNumber: this.fillNumber,

            orderId: this.orderId,
            subscriptionId: this.subscriptionId,
            programId: this.programId,
            encounterId: this.encounterId,

            testMode: this.testMode
        }
    }

    isValid = () => {
        if (isStringEmpty(this.userId)) {
            throw new Error('userId is required.');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            let autoCreatedGroup = false;

            // if no prescriptionGroupId is provided, auto-create a solo PrescriptionGroup for this prescription
            if (!isStringNotEmpty(this.prescriptionGroupId)) {
                const submittedStatuses = ['SUBMITTED', 'PROCESSING', 'RECEIVED', 'QUEUED', 'PREVERIFICATION', 'FILLING', 'VERIFIED', 'FILLED', 'SHIPPED', 'DELIVERED', 'COMPLETED', 'CLOSED'];
                const filledStatuses = ['FILLED', 'SHIPPED', 'DELIVERED', 'COMPLETED', 'CLOSED'];
                const shippedStatuses = ['SHIPPED', 'DELIVERED', 'COMPLETED', 'CLOSED'];
                const deliveredStatuses = ['DELIVERED', 'COMPLETED', 'CLOSED'];

                let fillsSubmitted = submittedStatuses.includes(this.status) ? 1 : 0;
                let fillsFilled = filledStatuses.includes(this.status) ? 1 : 0;
                let fillsShipped = shippedStatuses.includes(this.status) ? 1 : 0;
                let fillsDelivered = deliveredStatuses.includes(this.status) ? 1 : 0;
                let fillsCompleted = this.status === 'COMPLETED' ? 1 : 0;
                let fillsVoided = (this.status === 'VOIDED' || this.status === 'CANCELLED') ? 1 : 0;

                let groupStatus = 'ACTIVE';
                if (fillsCompleted > 0) { groupStatus = 'COMPLETED'; }
                else if (fillsVoided > 0) { groupStatus = 'VOIDED'; }

                const newGroup = await new PrescriptionGroup({
                    eRXId: this.eRXId,

                    userId: this.userId,
                    customer: this.customer,

                    practiceId: this.practiceId,
                    pharmacyId: this.pharmacyId,

                    providerId: this.providerId,
                    providerFirstName: this.providerFirstName,
                    providerLastName: this.providerLastName,
                    providerNPI: this.providerNPI,

                    medicationId: this.medicationId,
                    productId: this.productId,
                    variantId: this.variantId,
                    name: this.name,
                    strength: this.strength,
                    form: this.form,
                    route: this.route,
                    instructions: this.instructions,
                    quantity: this.quantity,
                    count: this.count,
                    countUnits: this.countUnits,
                    daysSupply: this.daysSupply,
                    medRefId: this.medRefId,
                    pdfRef: this.pdfRef,
                    providerSignatureRef: this.providerSignatureRef,

                    totalFills: 1,
                    fillsSubmitted: fillsSubmitted,
                    fillsFilled: fillsFilled,
                    fillsShipped: fillsShipped,
                    fillsDelivered: fillsDelivered,
                    fillsCompleted: fillsCompleted,
                    fillsVoided: fillsVoided,

                    prescriptionIds: [],
                    orderIds: [],

                    status: groupStatus,

                    writtenAt: this.writtenAt,
                    expirationDate: this.expirationDate,
                    lastFilledAt: this.filledAt,
                    lastDeliveredAt: this.deliveredAt,
                    nextFillAt: !['COMPLETED', 'VOIDED', 'CANCELLED', 'DELIVERED', 'SHIPPED'].includes(this.status) ? this.fillAt : null,
                    completedAt: groupStatus === 'COMPLETED' ? this.completedAt : null,
                    voidedAt: groupStatus === 'VOIDED' ? this.voidedAt : null,

                    isControlled: this.isControlled,
                    isCompleted: groupStatus === 'COMPLETED',
                    isExpired: false,
                    isVoided: groupStatus === 'VOIDED',

                    subscriptionId: this.subscriptionId,
                    programId: this.programId,
                    encounterId: this.encounterId,

                    testMode: this.testMode
                }).create();

                if (isObjectNotEmpty(newGroup) && isStringNotEmpty(newGroup.id)) {
                    this.prescriptionGroupId = newGroup.id;
                    this.fillNumber = 1;
                    autoCreatedGroup = true;
                }
            }

            const newPrescription = await create(Prescription.collectionName, this.toDto());

            // if we auto-created the group, update it with the new prescription's id and orderId
            if (autoCreatedGroup && isObjectNotEmpty(newPrescription) && isStringNotEmpty(newPrescription.id) && isStringNotEmpty(this.prescriptionGroupId)) {
                try {
                    let updateData = {
                        prescriptionIds: [newPrescription.id]
                    };
                    if (isStringNotEmpty(this.orderId)) {
                        updateData.orderIds = [this.orderId];
                    }
                    await PrescriptionGroup.updateFields(this.prescriptionGroupId, updateData);
                } catch (err) {
                    console.log('Error updating PrescriptionGroup with prescription id:', err.message);
                }
            }

            return newPrescription;
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Prescription.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(Prescription.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedEncounter) => {
        const updatedObj = new Prescription(updatedEncounter);
        if (updatedObj.isValid() && id === updatedEncounter.id) {
            return await update(Prescription.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated prescription must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Prescription.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Prescription.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getByRefId = async (refId) => {
        if (isStringNotEmpty(refId)) {
            let matches = await getBySingleQuery(Prescription.collectionName, 'refId', refId, '==');
            if (isListNotEmpty(matches)) {
                return matches[0];
            }

            return null;
        } else {
            throw new Error("refId is required.");
        }
    }

    static getByERxId = async (eRXId) => {
        if (isStringNotEmpty(eRXId)) {
            let matches = await getBySingleQuery(Prescription.collectionName, 'eRXId', eRXId, '==');
            if (isListNotEmpty(matches)) {
                return matches[0];
            }

            return null;
        } else {
            throw new Error("eRXId is required.");
        }
    }

    static getByFillId = async (fillId) => {
        if (isStringNotEmpty(fillId)) {
            return await getBySingleQuery(Prescription.collectionName, 'fillId', fillId, '==');
        } else {
            throw new Error("fillId is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Prescription.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Prescription.collectionName, fieldName, fieldValue, comparer);
    }

    static getByUserId = async (userId) => {
        return await getBySingleQuery(Prescription.collectionName, 'userId', userId, '==');
    }

    static getByUserIdAndStatus = async (userId, status) => {
        return await getByDoubleQuery(Prescription.collectionName, 'userId', userId, '==', 'status', status, '==');
    }

    static getBySubscriptionId = async (subscriptionId) => {
        return await getBySingleQuery(Prescription.collectionName, 'subscriptionId', subscriptionId, '==');
    }

    static getByUserIdAndSubscriptionId = async (userId, subscriptionId) => {
        return await getByDoubleQuery(Prescription.collectionName, 'userId', userId, '==', 'subscriptionId', subscriptionId, '==');
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Prescription.collectionName, ids, 'in');
    }

    static getByQueryConstraints = async (queryConstraints = []) => {
        let q = query(collection(db, Prescription.collectionName), ...queryConstraints);

        const snapShot = await getDocs(q);
        // console.log(snapShot)
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id, // append document id to each document
                ...doc.data()
            };
        });
    }

    static getDelayedForUsers = async (userIds = []) => {
        let startDate = moment.tz('US/Pacific').add(-24, 'hours').utc().toDate();

        return new Promise((res) => {
            // don't run if there aren't any ids
            if (!userIds || !userIds.length) return res([]);

            let batches = [];

            while (userIds.length) {
                // firestore limits batches to 10
                const batch = userIds.splice(0, 10);

                // add the batch request to to a queue
                batches.push(
                    new Promise(response => { getByQuery(query(collection(db, Prescription.collectionName), where('status', 'in', ['SUBMITTED', 'RECEIVED', 'QUEUED', 'PREVERIFICATION', 'FILLING', 'VERIFIED', 'FILLED', 'SHIPPED']), where('userId', 'in', [...batch]), where('submittedAt', '<=', startDate), orderBy('submittedAt', 'asc'), orderBy('__name__', 'asc'))).then(results => response(results)) })
                );
            }

            // after all of the data is fetched, return it
            Promise.all(batches).then(content => {
                res(content.flat());
            });
        });
    }

    static getDelayed = async (userId = null) => {
        let queryConstraints = [];

        let startDate = moment.tz('US/Pacific').add(-24, 'hours').utc().toDate();

        if (isStringNotEmpty(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }
        queryConstraints.push(where('status', 'in', ['SUBMITTED', 'RECEIVED', 'QUEUED', 'PREVERIFICATION', 'FILLING', 'VERIFIED', 'FILLED', 'SHIPPED']));
        queryConstraints.push(where('submittedAt', '<=', startDate));

        queryConstraints.push(orderBy('submittedAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getDelayedCount = async (userId = null) => {
        let queryConstraints = [];

        let startDate = moment.tz('US/Pacific').add(-24, 'hours').utc().toDate();

        if (isStringNotEmpty(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }
        queryConstraints.push(where('status', 'in', ['SUBMITTED', 'RECEIVED', 'QUEUED', 'PREVERIFICATION', 'FILLING', 'VERIFIED', 'FILLED', 'SHIPPED']));
        queryConstraints.push(where('submittedAt', '<=', startDate));

        queryConstraints.push(orderBy('submittedAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        let q = query(collection(db, Prescription.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static search = async (searchCriteria) => {
        let { providerId, practiceId, pharmacyId, firstName, lastName, email, phone, prescriptionId, prescriptionRefId } = searchCriteria;

        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(providerId)) {
            queryConstraints.push(where('providerId', '==', providerId));
        }
        if (isStringNotEmptyOrZeroLength(practiceId)) {
            queryConstraints.push(where('practiceId', '==', practiceId));
        }
        if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }
        if (isStringNotEmptyOrZeroLength(firstName)) {
            queryConstraints.push(where('customer.firstNameLower', '==', toLower(firstName)));
        }
        if (isStringNotEmptyOrZeroLength(lastName)) {
            queryConstraints.push(where('customer.lastNameLower', '==', toLower(lastName)));
        }
        if (isStringNotEmptyOrZeroLength(email)) {
            queryConstraints.push(where('customer.email', '==', toLower(email)));
        }
        if (isStringNotEmptyOrZeroLength(phone)) {
            const cleanPhone = toPhoneNumberLocal(phone);
            const formattedPhone = toE164PhoneNumber(phone);
            queryConstraints.push(where('customer.phone', 'in', [phone, formattedPhone, cleanPhone]));
        }
        if (isStringNotEmptyOrZeroLength(prescriptionId)) {
            queryConstraints.push(where(documentId(), '==', prescriptionId));
        }
        if (isStringNotEmptyOrZeroLength(prescriptionRefId)) {
            queryConstraints.push(where('refId', '==', prescriptionRefId));
        }

        queryConstraints.push(orderBy('createdAt', 'asc'));
        queryConstraints.push(orderBy('fillAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getCount = async (queryParameters) => {
        const { productId, status, userId, providerId, practiceId, pharmacyId, orderId, subscriptionId, programId, encounterId, isPaid, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productId', 'in', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productId', '==', productId));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(providerId)) {
            queryConstraints.push(where('providerId', 'in', providerId));
        } else if (isStringNotEmptyOrZeroLength(providerId)) {
            queryConstraints.push(where('providerId', '==', providerId));
        }

        if (isListNotEmpty(practiceId)) {
            queryConstraints.push(where('practiceId', 'in', practiceId));
        } else if (isStringNotEmptyOrZeroLength(practiceId)) {
            queryConstraints.push(where('practiceId', '==', practiceId));
        }

        if (isListNotEmpty(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', 'in', pharmacyId));
        } else if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(orderId)) {
            queryConstraints.push(where('orderId', 'in', orderId));
        } else if (isStringNotEmptyOrZeroLength(orderId)) {
            queryConstraints.push(where('orderId', '==', orderId));
        }

        if (isListNotEmpty(subscriptionId)) {
            queryConstraints.push(where('subscriptionId', 'in', subscriptionId));
        } else if (isStringNotEmptyOrZeroLength(subscriptionId)) {
            queryConstraints.push(where('subscriptionId', '==', subscriptionId));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(encounterId)) {
            queryConstraints.push(where('encounterId', 'in', encounterId));
        } else if (isStringNotEmptyOrZeroLength(encounterId)) {
            queryConstraints.push(where('encounterId', '==', encounterId));
        }

        if (isBooleanTrue(isPaid)) {
            queryConstraints.push(where('isPaid', '==', true));
        } else if (isBooleanFalse(isPaid)) {
            queryConstraints.push(where('isPaid', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = query(collection(db, Prescription.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { productId, status, userId, providerId, practiceId, pharmacyId, orderId, subscriptionId, programId, encounterId, isPaid, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productId', 'in', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productId', '==', productId));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(providerId)) {
            queryConstraints.push(where('providerId', 'in', providerId));
        } else if (isStringNotEmptyOrZeroLength(providerId)) {
            queryConstraints.push(where('providerId', '==', providerId));
        }

        if (isListNotEmpty(practiceId)) {
            queryConstraints.push(where('practiceId', 'in', practiceId));
        } else if (isStringNotEmptyOrZeroLength(practiceId)) {
            queryConstraints.push(where('practiceId', '==', practiceId));
        }

        if (isListNotEmpty(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', 'in', pharmacyId));
        } else if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(orderId)) {
            queryConstraints.push(where('orderId', 'in', orderId));
        } else if (isStringNotEmptyOrZeroLength(orderId)) {
            queryConstraints.push(where('orderId', '==', orderId));
        }

        if (isListNotEmpty(subscriptionId)) {
            queryConstraints.push(where('subscriptionId', 'in', subscriptionId));
        } else if (isStringNotEmptyOrZeroLength(subscriptionId)) {
            queryConstraints.push(where('subscriptionId', '==', subscriptionId));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(encounterId)) {
            queryConstraints.push(where('encounterId', 'in', encounterId));
        } else if (isStringNotEmptyOrZeroLength(encounterId)) {
            queryConstraints.push(where('encounterId', '==', encounterId));
        }

        if (isBooleanTrue(isPaid)) {
            queryConstraints.push(where('isPaid', '==', true));
        } else if (isBooleanFalse(isPaid)) {
            queryConstraints.push(where('isPaid', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Prescription.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Prescription.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, Prescription.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;

            let totalCount = await Prescription.getCount(queryParameters);
            let totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await Prescription.getPaginated(queryParameters, 10000, null, next, 'createdAt');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }

    static updatePrescriptions = async () => {
        let nextPage = null;
        let successCount = 0;
        let skipCount = 0;
        let totalPages = 0;
        let totalCount = await Prescription.getCount({});
        totalPages = Math.ceil(totalCount / 500.0);
        console.log(totalCount);
        console.log(totalPages);
        let pages = [];
        for (let i = 0; i < totalPages; i++) {
            pages.push(i + 1);
        }

        for (const page of pages) {
            console.log('starting page ' + page);
            const list = await Prescription.getPaginated({}, 500, null, nextPage, 'createdAt');
            console.log('fetched prescriptions: ' + list.length);

            var batch = writeBatch(db);
            list.map((item) => {
                const prescriptionRef = doc(collection(db, Prescription.collectionName), item.id);

                if (isNullOrUndefined(item?.expirationDate) || isNullOrUndefined(item?.isControlled)) {
                    let isControlled = false;
                    let expirationDays = isControlled ? 182 : 365;
                    let expirationDate = fromUtcToStartDateMoment(item?.fillAt).add(expirationDays, 'days').utc().toDate();

                    let updatedFields = {
                        expirationDate: expirationDate,
                        isControlled: false
                    };

                    batch.update(prescriptionRef, updatedFields);
                    successCount++;
                } else {
                    skipCount++;
                }
            });

            await batch.commit();

            console.log('go to next page', successCount, skipCount);
            nextPage = list[list.length - 1];
        }
    }
};

export default Prescription;
